import { create } from 'zustand';
import type { CollectionDeck, CollectionDeckSummary } from '@mtg-deck-tool/shared';
import {
  api,
  type AnalyticsOverview,
  type ColorIdentityCoverage,
  type TagCoverageItem,
  type StrategyGap,
  type DeckStrategyProfile,
} from '../lib/api-client.js';

interface CollectionStoreState {
  decks: CollectionDeckSummary[];
  activeDeck: CollectionDeck | null;
  isLoading: boolean;
  error: string | null;

  // Analytics Caching State
  analyticsData: {
    overview: AnalyticsOverview;
    colors: ColorIdentityCoverage[];
    tags: TagCoverageItem[];
    gaps: StrategyGap[];
    deckStrategies: DeckStrategyProfile[];
  } | null;
  analyticsLoading: boolean;
  analyticsError: string | null;
  analyticsDirty: boolean;

  fetchCollection: () => Promise<void>;
  loadDeck: (id: string) => Promise<CollectionDeck | null>;
  saveDeck: (deck: Omit<CollectionDeck, 'createdAt' | 'updatedAt' | 'syncedAt'>) => Promise<void>;
  deleteDeck: (id: string) => Promise<void>;
  deleteDecks: (ids: string[]) => Promise<void>;
  syncDeck: (id: string) => Promise<CollectionDeck | null>;
  importMoxfield: (moxfieldDeckId: string) => Promise<CollectionDeck | null>;
  fetchAnalytics: (force?: boolean) => Promise<void>;
}

export const useCollectionStore = create<CollectionStoreState>((set, get) => ({
  decks: [],
  activeDeck: null,
  isLoading: false,
  error: null,

  analyticsData: null,
  analyticsLoading: false,
  analyticsError: null,
  analyticsDirty: true,

  fetchCollection: async () => {
    set({ isLoading: true, error: null });
    try {
      const decks = await api.getCollectionDecks();
      set({ decks, isLoading: false });
    } catch (err: any) {
      set({ error: err.message || 'Failed to fetch collection', isLoading: false });
    }
  },

  loadDeck: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const activeDeck = await api.getCollectionDeck(id);
      set({ activeDeck, isLoading: false });
      return activeDeck;
    } catch (err: any) {
      set({ error: err.message || 'Failed to load deck', isLoading: false });
      return null;
    }
  },

  saveDeck: async (deck) => {
    set({ isLoading: true, error: null, analyticsDirty: true });
    try {
      const dbEntries = [
        ...deck.entries.map((e) => ({
          cardName: e.card.name,
          oracleId: e.card.oracleId,
          setCode: e.card.set,
          collectorNumber: e.card.collectorNumber,
          quantity: e.quantity,
          isCommander: e.isCommander,
          isSideboard: false,
        })),
        ...deck.sideboard.map((s) => ({
          cardName: s.card.name,
          oracleId: s.card.oracleId,
          setCode: s.card.set,
          collectorNumber: s.card.collectorNumber,
          quantity: 1,
          isCommander: false,
          isSideboard: true,
        })),
      ];

      const dbPayload = {
        id: deck.id,
        moxfieldDeckId: deck.moxfieldDeckId,
        name: deck.name,
        colorIdentity: deck.colorIdentity,
        commanderNames: deck.commanderNames,
        cardCount: deck.cardCount,
        entries: dbEntries,
      };

      await api.saveCollectionDeck(dbPayload);
      await get().fetchCollection();
      set({ isLoading: false });
    } catch (err: any) {
      set({ error: err.message || 'Failed to save deck', isLoading: false });
    }
  },

  deleteDeck: async (id: string) => {
    set({ isLoading: true, error: null, analyticsDirty: true });
    try {
      await api.deleteCollectionDeck(id);
      if (get().activeDeck?.id === id) {
        set({ activeDeck: null });
      }
      await get().fetchCollection();
      set({ isLoading: false });
    } catch (err: any) {
      set({ error: err.message || 'Failed to delete deck', isLoading: false });
    }
  },

  deleteDecks: async (ids: string[]) => {
    if (ids.length === 0) return;
    set({ isLoading: true, error: null, analyticsDirty: true });
    try {
      await api.deleteCollectionDecks(ids);
      const activeDeck = get().activeDeck;
      if (activeDeck && ids.includes(activeDeck.id)) {
        set({ activeDeck: null });
      }
      await get().fetchCollection();
      set({ isLoading: false });
    } catch (err: any) {
      set({ error: err.message || 'Failed to delete decks', isLoading: false });
    }
  },

  syncDeck: async (id: string) => {
    set({ isLoading: true, error: null, analyticsDirty: true });
    try {
      const updated = await api.syncCollectionDeck(id);
      if (get().activeDeck?.id === id) {
        set({ activeDeck: updated });
      }
      await get().fetchCollection();
      set({ isLoading: false });
      return updated;
    } catch (err: any) {
      set({ error: err.message || 'Failed to sync deck with Moxfield', isLoading: false });
      return null;
    }
  },

  importMoxfield: async (moxfieldDeckId: string) => {
    set({ isLoading: true, error: null, analyticsDirty: true });
    try {
      const imported = await api.importMoxfieldDeck(moxfieldDeckId);
      await get().fetchCollection();
      set({ isLoading: false });
      return imported;
    } catch (err: any) {
      set({ error: err.message || 'Failed to import deck from Moxfield', isLoading: false });
      return null;
    }
  },

  fetchAnalytics: async (force = false) => {
    if (get().analyticsData && !get().analyticsDirty && !force) {
      return;
    }
    set({ analyticsLoading: true, analyticsError: null });
    try {
      const [overview, colors, tags, gaps, deckStrategies] = await Promise.all([
        api.getAnalyticsOverview(),
        api.getColorCoverage(),
        api.getTagCoverage(),
        api.getStrategyGaps(),
        api.getDeckStrategies(),
      ]);
      set({
        analyticsData: { overview, colors, tags, gaps, deckStrategies },
        analyticsDirty: false,
        analyticsLoading: false,
      });
    } catch (err: any) {
      set({
        analyticsError: err.message || 'Failed to fetch portfolio analytics',
        analyticsLoading: false,
      });
    }
  },
}));
